
import React from 'react'
import { withComponentContext } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { Grid, Typography } from '@mui/material'
import { compose } from 'widgets-shared/core'
import HelperInfo from 'smartstream-widgets/controls/HelperInfo/HelperInfo'

import { HeaderProps } from '../types'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

const styles = (theme: Theme) =>  createStyles({
  competitionLogo: {
    verticalAlign: 'bottom'
  },
  competitionName: {
    fontSize: 11
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  helperInfoButton: {
    cursor: 'pointer',
    color: theme.palette.action.active
  },
  helperInfoContent: {
    fontSize: 14
  },
  helperInfoDisclaimer: {
    fontStyle: 'italic',
    marginTop: 15
  },
  helperInfoFooter: {
    bottom: 5,
    left: 0,
    position: 'absolute'
  },
  helperInfoFooterLogo: {
    height: 15,
    marginRight: 5,
    width: 15
  },
  helperInfoFooterText: {
    fontSize: 9
  },
  helperInfoModal: {
    color: theme.palette.text.primary,
    '& > div:nth-child(3)': {
      bottom: 0
    },
    '& p': {
      marginBottom: 5
    }
  },
  helperInfoTitle: {
    fontWeight: 'bold'
  },
  marketName: {
    fontFamily: 'inherit',
    fontSize: 16,
    fontWeight: 'inherit',
    lineHeight: 'inherit'
  },
  root: {
    color: theme.palette.text.primary,
    fontFamily: `${theme.typography.variantFontFamily}, ${theme.typography.fontFamily}`,
    fontWeight: 'bold',
    lineHeight: 1,
    padding: `4px ${theme.spacing(4)} 6px`,
    textTransform: 'uppercase'
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit'
  }
})

export function FuturesHeader (props: HeaderProps) {
  const {
    classes,
    competitionName,
    config: {
      BASE_ASSET_PATH,
      showWidgetTitle = true
    },
    containerRef,
    marketName,
    translations
  } = props

  const helperInfoWithContent = (
    <HelperInfo
      anchorEl={containerRef ? () => containerRef.current : null}
      classes={{
        closeButton: classes.helperInfoButton,
        modal: classes.helperInfoModal,
        openButton: classes.helperInfoButton,
        title: classes.helperInfoTitle
      }}
      closeIcon={`${BASE_ASSET_PATH}/img/icons.svg#ic-close`}
      openIcon={`${BASE_ASSET_PATH}/img/icons.svg#ic-message-info`}
      title={translations.aboutWidgetTitle}
    >
      <Typography className={classes.helperInfoContent} gutterBottom>{translations.helperInfoContent}</Typography>
      <Typography className={classes.helperInfoDisclaimer} gutterBottom>
        {translations.helperInfoDisclaimer}
      </Typography>
      <Grid container className={classes.helperInfoFooter} justifyContent='center'>
        <img
          className={classes.helperInfoFooterLogo}
          src={`${BASE_ASSET_PATH}/img/genius-sports-logos/gs-logo-footer-25px.png`}
          alt='Genius Sports Media' />
        <Typography className={classes.helperInfoFooterText}>
          {translations.helperInfoFooterText}
        </Typography>
      </Grid>
    </HelperInfo>
  )

  const widgetTitle = `${translations.WidgetTitle} - ${competitionName}`

  return (
    <Grid
      container
      component='header'
      className={classes.root}
      direction='column'
      justifyContent='center'
      wrap='nowrap'
      data-testid='futures-header-container'
    >
      {
        showWidgetTitle &&
        <Typography className={`${classes.titleContainer} ${classes.competitionName}`} title={widgetTitle}>
          <span className={classes.ellipsis}>
            {widgetTitle}
          </span>
          {helperInfoWithContent}
        </Typography>
      }
      <Typography className={`${classes.titleContainer} ${classes.marketName}`} title={marketName}>
        <span className={classes.ellipsis}>
          {marketName}
        </span>
        {showWidgetTitle ? null : helperInfoWithContent}
      </Typography>
    </Grid>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'FuturesHeader' , defaultTheme: getDefaultTheme}),
  withTranslations((t: TranslationFunction) => {
    return {
      WidgetTitle: t('title.translation', {
        defaultValue: 'Futures',
        context: 'Text to display as title in the widget previous to the competition'
      }),
      aboutWidgetTitle: t('about-title.translation', {
        defaultValue: 'About Futures',
        context: 'Text to display as title in the info popup in Futures widget'
      }),
      helperInfoContent: t('about-info.translation', {
        defaultValue: 'A future bet is any bet made for sometime in the future. Most future bets are related to winning some sort a Championship but a future bet can be made on almost anything that will occur at a future date.',
        context: 'Text to display as content inside info popup in Futures widget'
      }),
      helperInfoDisclaimer: t('about-odds.translation', {
        defaultValue: 'Odds are subject to change at any time.',
        context: 'Text to display inside info popup as disclaimer regarding odds update in Futures widget'
      }),
      helperInfoFooterText: t('about-footer.translation', {
        defaultValue: 'Powered by Genius Sports Media',
        context: 'Text to display in the info popup footer in Futures widget'
      })
    }
  })
)(FuturesHeader)
